import { type MouseEvent } from "react";
import { PhoneOutgoingIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { ClipboardArrowUpRight } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { storage } from "@/lib/storage";
import { cn } from "@/lib/utils";

// Quo call action: open the existing conversation when we have one stored,
// otherwise copy the phone number and open the user's Quo inbox. Renders
// nothing when the current user has no Quo inbox.
export const QuoQuickAction = ({
  phone,
  quoConversationId,
  screenName,
  mixpanelMetadata,
  className,
}: {
  phone: string | null;
  quoConversationId: string | null;
  screenName: MIXPANEL_SCREENS;
  mixpanelMetadata?: Record<string, unknown>;
  className?: string;
}) => {
  const currentUserId = storage.getUser()?.id;
  const { users } = useTaggableUsers();
  const currentUserQuoPhoneNumberId =
    users.find((u) => u.id === currentUserId)?.quoPhoneNumberId ?? null;

  const canOpenInQuo = !!currentUserQuoPhoneNumberId && !!quoConversationId;
  const canCopyAndOpenInbox =
    !!currentUserQuoPhoneNumberId && !quoConversationId && !!phone;

  /* Handlers */
  const handleOpenInQuo = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!currentUserQuoPhoneNumberId || !quoConversationId) return;
    mixpanelUtils.trackButtonClick("Open in Quo", screenName, mixpanelMetadata);
    window.open(
      `https://my.quo.com/inbox/${currentUserQuoPhoneNumberId}/c/${quoConversationId}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleCopyAndOpenInbox = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!currentUserQuoPhoneNumberId || !phone) return;
    mixpanelUtils.trackButtonClick(
      "Copy Phone and Open Quo Inbox",
      screenName,
      mixpanelMetadata,
    );
    void navigator.clipboard.writeText(phone);
    window.open(
      `https://my.quo.com/inbox/${currentUserQuoPhoneNumberId}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  /* Render */
  const render = () => {
    if (!currentUserQuoPhoneNumberId) return null;

    if (canCopyAndOpenInbox) {
      return (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className={cn(
                  "h-7 w-7 text-slate-500 hover:bg-slate-100 hover:text-slate-900",
                  className,
                )}
                onClick={handleCopyAndOpenInbox}
                size="icon"
                variant="ghost"
              >
                <ClipboardArrowUpRight className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Copy phone number and open Quo inbox
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                className={cn(
                  "h-7 w-7 text-slate-500 hover:bg-slate-100 hover:text-slate-900",
                  className,
                )}
                disabled={!canOpenInQuo}
                onClick={handleOpenInQuo}
                size="icon"
                variant="ghost"
              >
                <PhoneOutgoingIcon className="h-3.5 w-3.5" />
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {canOpenInQuo
              ? "Open in Quo"
              : "You don't have a stored quo conversation with this candidacy"}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return render();
};
